import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A confirmation that clears itself.
 *
 * "llm.default_model saved" used to sit on the screen indefinitely. It also
 * survived a tab switch, which is a consequence of panes now staying mounted:
 * unmounting a pane used to clear it by accident, so nothing ever cleared it
 * on purpose. A stale confirmation is worse than none — it says "saved" long
 * after the save, so the next time it appears you cannot tell whether it is
 * about what you just did.
 *
 * The timer is reset each time a new message arrives, so two saves in quick
 * succession do not leave the second one cut short by the first one's clock.
 */
export function useNotice(ms = 4000): [string | null, (message: string | null) => void] {
  const [notice, setNotice] = useState<string | null>(null)
  const timer = useRef<number | null>(null)

  const show = useCallback(
    (message: string | null) => {
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = null
      setNotice(message)
      if (message) {
        timer.current = window.setTimeout(() => {
          timer.current = null
          setNotice(null)
        }, ms)
      }
    },
    [ms],
  )

  // A pending timeout that fires after unmount would set state on a dead
  // component. Panes are long-lived now, but this hook must not assume it.
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current)
    },
    [],
  )

  return [notice, show]
}
